import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCaptureMonitoringRecord, deleteCaptureMonitoringRecord, getCaptureMonitoringRecords, updateCaptureMonitoringRecord } from "../../api/captures/captureMonitoring";
import { MonitoringRecordForm } from "../../types/forms/capture";
import { useState } from "react";

interface FilterOptions {
  variableId?: number;
  startDate?: string;
  endDate?: string;
}

export const useCaptureMonitoring = (captureIdStr?: string) => {
  const queryClient = useQueryClient();
  const captureId = Number(captureIdStr);

  const [page, setPage] = useState(0);
  const [filtersState, setFiltersState] = useState<FilterOptions>({});

  const setFilters = (newFilters: FilterOptions) => {
    setFiltersState(newFilters);
    setPage(0);
  };

  const queryKey = ["capture", captureId, "monitoring", filtersState];

  const result = useQuery({
    queryKey,
    queryFn: () => getCaptureMonitoringRecords(captureId, filtersState.variableId, filtersState.startDate, filtersState.endDate),
    enabled: !!captureId,
    placeholderData: keepPreviousData,
  });

  const createMutation = useMutation({
    mutationFn: (values: MonitoringRecordForm) => createCaptureMonitoringRecord(captureId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ monitoringId, values }: { monitoringId: number; values: MonitoringRecordForm }) => updateCaptureMonitoringRecord(captureId, monitoringId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (monitoringId: number) => deleteCaptureMonitoringRecord(captureId, monitoringId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    monitoringRecordsData: result.data ?? [],
    isPending: result.isPending,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
    filters: filtersState,
    setFilters,
    page,
    setPage,
    createMonitoring: createMutation.mutateAsync,
    updateMonitoring: updateMutation.mutateAsync,
    deleteMonitoring: deleteMutation.mutateAsync,
    isUpdating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
};
